import  { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { authService } from '../auth/authService';
import { forgotPasswordSchema, ForgotPasswordFormData } from '../validations/auth';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const response = await authService.forgotPassword({
        email: data.email,
      });

      if (response.success) {
        setSuccessMessage('Password reset instructions have been sent to your email. Please check your inbox.');
        // Navigate to verify OTP page after a short delay
        setTimeout(() => {
          navigate('/verify-otp', { 
            state: { email: data.email },
            replace: true 
          });
        }, 2000);
      } else {
        setErrorMessage(response.message || 'Failed to send password reset email. Please try again.');
      }
    } catch (error: unknown) {
      console.error('Forgot password error:', error);
      
      // Handle specific error types
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
      
      if (axiosError.response?.status === 404) {
        setError('email', {
          type: 'manual',
          message: 'No account found with this email address',
        });
      } else if (axiosError.response?.status === 422) {
        setError('email', {
          type: 'manual',
          message: 'Please check your email address and try again',
        });
      } else if (axiosError.response?.status && axiosError.response.status >= 500) {
        setErrorMessage('Server error. Please try again later.');
      } else {
        setErrorMessage(axiosError.response?.data?.message || 'Failed to send password reset email. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container
      maxWidth="xs"
      sx={{
        py: 8,
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box textAlign="center" mb={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Forgot Password
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Enter your email address to receive a verification code
          </Typography>
        </Box>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email Address"
                type="email"
                fullWidth
                margin="normal"
                error={!!errors.email}
                helperText={errors.email?.message}
                disabled={isLoading}
                autoComplete="email"
              />
            )}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}
            sx={{
              bgcolor: '#b8957b',
              color: 'white',
              fontWeight: 700,
              '&:hover': { bgcolor: '#a98c74' },
              '&:disabled': { bgcolor: '#ccc' },
              mt: 3,
              mb: 2
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Send Code'
            )}
          </Button>
        </form>

        <Box textAlign="center" mt={2}>
          <Link
            href="/sign-in"
            variant="body2"
            sx={{
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Back to Sign In
          </Link>
        </Box>
      </Box>
    </Container>
  );
}
